/**
 * Observe-only SDTestApp client via Hub bench relay (remote control level).
 */

import { apiFetch } from "@/lib/api";

import type { SdTestDemographics, SdTestStatus } from "./sdTestAppHttpClient";

export interface SdTestRelayClientListeners {
  onStatusChange?: (status: SdTestStatus) => void;
}

const STATUS_OFFLINE: SdTestStatus = { text: "Bench offline (relay)", color: "#DC3545" };
const STATUS_CONNECTED: SdTestStatus = { text: "Bench connected (observe)", color: "#17A2B8" };

function relayUrl(machineId: string, hubApiPath: string): string {
  const normalizedPath = hubApiPath.replace(/^\/+/, "");
  return `/api/benches/${encodeURIComponent(machineId)}/relay/${normalizedPath}`;
}

export class SdTestAppRelayClient {
  private machineId: string;
  private listeners: SdTestRelayClientListeners;
  private status: SdTestStatus = STATUS_OFFLINE;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(machineId: string, listeners: SdTestRelayClientListeners = {}) {
    this.machineId = String(machineId ?? "").trim();
    this.listeners = listeners;
  }

  getStatus(): SdTestStatus {
    return this.status;
  }

  startPolling(intervalMilliseconds = 5000): void {
    this.stopPolling();
    void this.refresh();
    this.pollTimer = setInterval(() => {
      void this.refresh();
    }, intervalMilliseconds);
  }

  stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  dispose(): void {
    this.stopPolling();
  }

  private setStatus(status: SdTestStatus): void {
    this.status = status;
    this.listeners.onStatusChange?.(status);
  }

  async refresh(): Promise<SdTestDemographics | null> {
    if (!this.machineId) {
      this.setStatus(STATUS_OFFLINE);
      return null;
    }

    try {
      await apiFetch<{ ok?: boolean }>(relayUrl(this.machineId, "/api/hub/v1/health"));
      const demographics = await apiFetch<SdTestDemographics>(relayUrl(this.machineId, "/api/hub/v1/devices"));
      const deviceCount = Array.isArray(demographics.devices) ? demographics.devices.length : 0;
      if (deviceCount > 0) {
        this.setStatus({ ...STATUS_CONNECTED, text: `Bench observe (${deviceCount} device${deviceCount === 1 ? "" : "s"})` });
      } else {
        this.setStatus({ ...STATUS_CONNECTED, text: "Bench connected (no active devices)" });
      }
      return demographics;
    } catch {
      this.setStatus(STATUS_OFFLINE);
      return null;
    }
  }
}
